import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import SideBar from "../../components/Sidebar";
import ProfileService from "../../services/ProfileService";
import PGService from "../../services/PGService";
import AddMoneyModal from "./AddMoneyModal";
import {
  FaWallet,
  FaUniversity,
  FaMobileAlt,
  FaMoneyBillWave,
  FaCopy,
  FaCheckCircle,
  FaBuilding,
  FaQrcode,
  FaHandHoldingUsd,
} from "react-icons/fa";
import { SiGooglepay, SiPhonepe, SiPaytm } from "react-icons/si";
import storage from "../../utils/storage";
import FundRequest from "./FundRequest";

const Wallet = () => {
  const [loading, setLoading] = useState(true);
  const [showUPIModal, setShowUPIModal] = useState(false);
  const [balanceData, setBalanceData] = useState(null);
  const [virtualAccounts, setVirtualAccounts] = useState([]);
  const [copiedField, setCopiedField] = useState(null); // Track which field was copied
  const [activeDepositMethod, setActiveDepositMethod] = useState("upi");
  const [cashRequestAmount, setCashRequestAmount] = useState("");
  const [cashRequestRemark, setCashRequestRemark] = useState("");
  const [submittingCashRequest, setSubmittingCashRequest] = useState(false);

  useEffect(() => {
    fetchBalances();
    fetchVirtualAccounts();
  }, []);

  const fetchBalances = async () => {
    setLoading(true);
    try {
      const result = await ProfileService.fetchBalance();
      if (!result.success) {
        Swal.fire("Oops!", result.message, "error");
        return;
      }
      const responseData = result.data;
      if (responseData.status === "SUCCESS") {
        setBalanceData(responseData.data);
      } else {
        Swal.fire("Oops!", responseData.message, "error");
      }
    } catch (error) {
      Swal.fire(
        "Oops!",
        "Request Failed: " + (error.response?.data || error.message),
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchVirtualAccounts = async () => {
    try {
      const result = await ProfileService.fetch();
      if (result.success && result.data?.status === "SUCCESS") {
        setVirtualAccounts(result?.data.data?.virtual_accounts || []);
      }
    } catch (error) {
      console.error("Failed to fetch virtual accounts", error);
      setVirtualAccounts([]);
    }
  };

  const handleCopy = (text, fieldId) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldId);
    setTimeout(() => setCopiedField(null), 2000);
    Swal.fire({
      icon: "success",
      title: "Copied!",
      text: "Details copied to clipboard",
      timer: 1500,
      showConfirmButton: false,
    });
  };

  const handleCashDepositRequest = async () => {
    if (!cashRequestAmount || parseFloat(cashRequestAmount) <= 0) {
      Swal.fire("Error", "Please enter a valid amount", "error");
      return;
    }
    setSubmittingCashRequest(true);
    try {
      const result = await PGService.requestCashDeposit({
        amount: cashRequestAmount,
        remark: cashRequestRemark,
      });
      if (!result.success) {
        Swal.fire("Oops!", result.message, "error");
        return;
      }
      const responseData = result.data;
      if (responseData.status === "SUCCESS") {
        Swal.fire(
          "Success!",
          "Cash deposit request submitted successfully",
          "success",
        );
        setCashRequestAmount("");
        setCashRequestRemark("");
        fetchBalances();
      } else {
        Swal.fire("Oops!", responseData.message, "error");
      }
    } catch (error) {
      Swal.fire(
        "Oops!",
        "Request Failed: " + (error.response?.data || error.message),
        "error",
      );
    } finally {
      setSubmittingCashRequest(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const getBankLogo = (bankName) => {
    const logos = {
      "SBM BANK": "/assets/images/sbm-bank-logo.png",
    };
    for (const [key, url] of Object.entries(logos)) {
      if (bankName?.toUpperCase().includes(key)) return url;
    }
    return null;
  };

  return (
    <div className="container-fluid mt-3">
      <div className="row">
        <SideBar />
        <div className="col-md-9">
          {/* Wallet Cards - Main & BC Wallet */}
          <div className="row g-3 mb-4">
            <div className="col-md-6">
              <div
                className="card h-100 shadow-sm border-0 rounded-4"
                style={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                }}
              >
                <div className="card-body p-4 text-white">
                  <div className="d-flex align-items-center mb-3">
                    <FaWallet size={32} className="text-white me-2" />
                    <h6 className="mb-0">Main Wallet Balance</h6>
                  </div>
                  <h2 className="fw-bold mb-0">
                    {formatCurrency(balanceData?.main_wallet)}
                  </h2>
                  <small className="opacity-75">
                    Available for recharge and other utility payment services
                  </small>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div
                className="card h-100 shadow-sm border-0 rounded-4"
                style={{
                  background:
                    "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                }}
              >
                <div className="card-body p-4 text-white">
                  <div className="d-flex align-items-center mb-3">
                    <FaUniversity size={32} className="text-white me-2" />
                    <h6 className="mb-0">BC Wallet Balance</h6>
                  </div>
                  <h2 className="fw-bold mb-0">
                    {formatCurrency(balanceData?.settlement_wallet)}
                  </h2>
                  <small className="opacity-75">Uses for DMT service</small>
                </div>
              </div>
            </div>
          </div>

          {/* Deposit Methods Tabs */}
          <div className="card shadow-sm border-0 rounded-4 mb-4">
            <div className="card-header bg-white border-0 pt-4 px-4">
              <ul className="nav nav-pills gap-2" role="tablist">
                <li className="nav-item">
                  <button
                    className={`nav-link rounded-pill px-4 ${activeDepositMethod === "upi" ? "active bg-primary" : "text-dark"}`}
                    onClick={() => setActiveDepositMethod("upi")}
                  >
                    <FaMobileAlt className="me-2" /> UPI
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link rounded-pill px-4 ${activeDepositMethod === "bank" ? "active bg-primary" : "text-dark"}`}
                    onClick={() => setActiveDepositMethod("bank")}
                  >
                    <FaUniversity className="me-2" /> Bank Transfer (E-Collect)
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link rounded-pill px-4 ${activeDepositMethod === "cash" ? "active bg-primary" : "text-dark"}`}
                    onClick={() => setActiveDepositMethod("cash")}
                  >
                    <FaHandHoldingUsd className="me-2" /> Fund Request (Cash
                    Deposit)
                  </button>
                </li>
              </ul>
            </div>
            <div className="card-body p-4">
              {/* UPI Panel */}
              {activeDepositMethod === "upi" && (
                <div className="text-center py-3">
                  <FaQrcode size={80} className="text-primary mb-3" />
                  <h5>Instant UPI Payment</h5>
                  <p className="text-muted">
                    Pay using any UPI app - Google Pay, PhonePe, Paytm
                  </p>
                  <div className="row justify-content-center g-3 mb-4">
                    <div className="col-auto">
                      <div
                        className="border rounded-3 p-3 text-center"
                        style={{ width: "120px" }}
                      >
                        <SiGooglepay size={40} className="text-primary mb-2" />
                        <div className="small fw-bold">Google Pay</div>
                      </div>
                    </div>
                    <div className="col-auto">
                      <div
                        className="border rounded-3 p-3 text-center"
                        style={{ width: "120px" }}
                      >
                        <SiPhonepe
                          size={40}
                          className="mb-2"
                          style={{ color: "#5F259F" }}
                        />
                        <div className="small fw-bold">PhonePe</div>
                      </div>
                    </div>
                    <div className="col-auto">
                      <div
                        className="border rounded-3 p-3 text-center"
                        style={{ width: "120px" }}
                      >
                        <SiPaytm
                          size={40}
                          className="mb-2"
                          style={{ color: "#00BAF2" }}
                        />
                        <div className="small fw-bold">Paytm</div>
                      </div>
                    </div>
                  </div>
                  <button
                    className="btn btn-success btn-lg px-5 rounded-pill shadow"
                    onClick={() => setShowUPIModal(true)}
                  >
                    <FaMobileAlt className="me-2" /> Add Money via UPI
                  </button>
                </div>
              )}

              {/* Bank Transfer (E-Collect) Panel - REDESIGNED */}
              {activeDepositMethod === "bank" && (
                <div>
                  <h5 className="mb-2">Bank Transfer via E-Collect</h5>
                  <p className="text-muted mb-4">
                    Register the below account as a beneficiary and transfer
                    funds using IMPS (recommended) / NEFT / RTGS.
                  </p>

                  {virtualAccounts.length === 0 ? (
                    <div className="alert alert-info">
                      <FaBuilding className="me-2" /> Loading bank details...
                    </div>
                  ) : (
                    <>
                      {/* Bank Account Cards */}
                      <div className="vstack gap-4 mb-4">
                        {virtualAccounts.map((va, idx) => {
                          const bankLogo = getBankLogo(va.bank);
                          return (
                            <div
                              key={idx}
                              className="card border-0 shadow-sm rounded-4"
                            >
                              {/* Bank Header */}
                              <div className="card-header bg-white border-0 pt-4 px-4">
                                <div className="d-flex align-items-center">
                                  {bankLogo ? (
                                    <img
                                      src={bankLogo}
                                      alt={va.bank}
                                      height="30"
                                      className="me-3"
                                    />
                                  ) : (
                                    <div className="bg-light rounded-circle p-2 me-3">
                                      <FaBuilding
                                        size={24}
                                        className="text-secondary"
                                      />
                                    </div>
                                  )}
                                  {/* <div>
                                    <h5 className="mb-0">{va.bank}</h5>
                                    <small className="text-muted">
                                      Virtual Account
                                    </small>
                                  </div> */}
                                </div>
                              </div>

                              <div className="card-body px-4 pb-4">
                                <div className="row g-4">
                                  {/* Main Wallet */}
                                  <div className="col-md-6">
                                    <div className="bg-light rounded-3 p-3 h-100">
                                      <div className="d-flex align-items-center mb-3">
                                        <FaWallet
                                          className="text-primary me-2"
                                          size={18}
                                        />
                                        <span className="fw-semibold">
                                          Main Wallet Account
                                        </span>
                                      </div>
                                      <div className="mb-2">
                                        <div className="small text-muted">
                                          Account Name
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center">
                                          <code className="fs-6">
                                            {va.account_name}
                                          </code>
                                          <button className="btn btn-sm btn-link text-secondary p-0"></button>
                                        </div>
                                      </div>
                                      <div className="mb-2">
                                        <div className="small text-muted">
                                          Account Number
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center">
                                          <code className="fs-6">
                                            {va.account_main_wallet}
                                          </code>
                                          <button
                                            className="btn btn-sm btn-link text-secondary p-0"
                                            onClick={() =>
                                              handleCopy(
                                                va.account_main_wallet,
                                                `main-${idx}`,
                                              )
                                            }
                                          >
                                            {copiedField === `main-${idx}` ? (
                                              <FaCheckCircle
                                                className="text-success"
                                                size={16}
                                              />
                                            ) : (
                                              <FaCopy size={14} />
                                            )}
                                          </button>
                                        </div>
                                      </div>
                                      <div>
                                        <div className="small text-muted">
                                          IFSC Code
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center">
                                          <code>{va.ifsc}</code>
                                          <button
                                            className="btn btn-sm btn-link text-secondary p-0"
                                            onClick={() =>
                                              handleCopy(
                                                va.ifsc,
                                                `ifsc-main-${idx}`,
                                              )
                                            }
                                          >
                                            {copiedField ===
                                            `ifsc-main-${idx}` ? (
                                              <FaCheckCircle
                                                className="text-success"
                                                size={16}
                                              />
                                            ) : (
                                              <FaCopy size={14} />
                                            )}
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* BC Wallet */}
                                  <div className="col-md-6">
                                    <div className="bg-light rounded-3 p-3 h-100">
                                      <div className="d-flex align-items-center mb-3">
                                        <FaUniversity
                                          className="text-success me-2"
                                          size={18}
                                        />
                                        <span className="fw-semibold">
                                          BC Wallet Account
                                        </span>
                                      </div>
                                      <div className="mb-2">
                                        <div className="small text-muted">
                                          Account Name
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center">
                                          <code className="fs-6">
                                            {va.account_name}
                                          </code>
                                          <button className="btn btn-sm btn-link text-secondary p-0"></button>
                                        </div>
                                      </div>
                                      <div className="mb-2">
                                        <div className="small text-muted">
                                          Account Number
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center">
                                          <code className="fs-6">
                                            {va.account_settlement_wallet}
                                          </code>
                                          <button
                                            className="btn btn-sm btn-link text-secondary p-0"
                                            onClick={() =>
                                              handleCopy(
                                                va.account_settlement_wallet,
                                                `settlement-${idx}`,
                                              )
                                            }
                                          >
                                            {copiedField ===
                                            `settlement-${idx}` ? (
                                              <FaCheckCircle
                                                className="text-success"
                                                size={16}
                                              />
                                            ) : (
                                              <FaCopy size={14} />
                                            )}
                                          </button>
                                        </div>
                                      </div>
                                      <div>
                                        <div className="small text-muted">
                                          IFSC Code
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center">
                                          <code>{va.ifsc}</code>
                                          <button
                                            className="btn btn-sm btn-link text-secondary p-0"
                                            onClick={() =>
                                              handleCopy(
                                                va.ifsc,
                                                `ifsc-settlement-${idx}`,
                                              )
                                            }
                                          >
                                            {copiedField ===
                                            `ifsc-settlement-${idx}` ? (
                                              <FaCheckCircle
                                                className="text-success"
                                                size={16}
                                              />
                                            ) : (
                                              <FaCopy size={14} />
                                            )}
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}
              {/* Cash Deposit Panel */}
              {activeDepositMethod === "cash" && <FundRequest />}
            </div>
          </div>

          {/* Tips Section */}
          <div className="row">
            <div className="col-md-4 mb-3">
              <div className="card h-100 shadow-sm border-0 rounded-3">
                <div className="card-body text-center py-4">
                  <i className="bi bi-lightning-charge-fill text-warning fs-2 mb-2"></i>
                  <h6 className="fw-semibold">Instant Topup</h6>
                  <p className="text-muted small mb-0">
                    UPI payments reflect instantly in your wallet.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4 mb-3">
              <div className="card h-100 shadow-sm border-0 rounded-3">
                <div className="card-body text-center py-4">
                  <i className="bi bi-shield-lock-fill text-primary fs-2 mb-2"></i>
                  <h6 className="fw-semibold">Secure Payments</h6>
                  <p className="text-muted small mb-0">
                    100% safe and secure transactions.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4 mb-3">
              <div className="card h-100 shadow-sm border-0 rounded-3">
                <div className="card-body text-center py-4">
                  <i className="bi bi-headset text-info fs-2 mb-2"></i>
                  <h6 className="fw-semibold">24x7 Support</h6>
                  <p className="text-muted small mb-0">
                    We're always here to help you.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {loading && (
            <div className="text-center mt-4">
              <div className="spinner-border text-primary" role="status" />
            </div>
          )}
        </div>
      </div>

      <AddMoneyModal
        isOpen={showUPIModal}
        onRequestClose={() => setShowUPIModal(false)}
        balanceData={balanceData}
        fetchBalance={fetchBalances}
      />
    </div>
  );
};

export default Wallet;
